<section class="d-flex flex-column gap-4" data-testid="subscription-control-page">
  <header class="d-flex flex-column gap-2">
    <a
      [routerLink]="organizationLink()"
      class="av-meta text-decoration-none d-inline-flex align-items-center gap-1"
      data-testid="control-back"
    >
      <mat-icon aria-hidden="true">arrow_back</mat-icon>
      <span>{{ 'backoffice.subscriptions.control.back' | translate }}</span>
    </a>

    <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
      <div class="d-flex flex-column gap-1">
        <h2 class="av-section-title m-0">{{ 'backoffice.subscriptions.control.title' | translate }}</h2>
        <p class="av-meta m-0">{{ 'backoffice.subscriptions.control.subtitle' | translate }}</p>
      </div>

      <button type="button" class="av-btn" (click)="reload()" data-testid="control-reload">
        <mat-icon aria-hidden="true">refresh</mat-icon>
        <span>{{ 'backoffice.common.reload' | translate }}</span>
      </button>
    </div>

    <p class="av-meta m-0" data-testid="control-scope-note">{{ 'backoffice.subscriptions.control.scope_note' | translate }}</p>

    @if (facade.organizationName()) {
      <p class="cell-strong m-0" data-testid="control-organization">
        {{ facade.organizationName() }}
      </p>
    }
  </header>

  @switch (facade.state()) {
    @case ('error') {
      <av-empty-state
        icon="error_outline"
        [title]="'backoffice.subscriptions.control.error_title' | translate"
        [message]="'backoffice.subscriptions.control.error_message' | translate"
      ></av-empty-state>
    }
    @case ('ready') {
      @if (facade.subscription(); as subscription) {
        @if (!facade.hasRole()) {
          <div class="av-banner p-3 d-flex align-items-start gap-2" data-testid="control-restricted">
            <mat-icon aria-hidden="true">visibility</mat-icon>
            <span>{{ 'backoffice.subscriptions.control.restricted' | translate }}</span>
          </div>
        }

        @if (!facade.isOwned()) {
          <div class="av-banner p-3 d-flex align-items-start gap-2" data-testid="control-not-owned">
            <mat-icon aria-hidden="true">report</mat-icon>
            <span>{{ 'backoffice.subscriptions.control.not_owned' | translate }}</span>
          </div>
        }

        <div class="av-card p-3 d-flex flex-column gap-3" data-testid="control-summary">
          <h3 class="av-section-title m-0">{{ 'backoffice.subscriptions.summary.title' | translate }}</h3>

          <div class="d-flex flex-wrap gap-4">
            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.subscriptions.summary.status' | translate }}</span>
              <av-status-chip
                [label]="subscription.statusLabelKey | translate"
                [tone]="subscription.statusTone"
              ></av-status-chip>
            </div>

            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.subscriptions.summary.plan' | translate }}</span>
              <span class="cell-strong" data-testid="summary-plan">{{ subscription.planLabel }}</span>
            </div>

            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.subscriptions.summary.offer' | translate }}</span>
              <span class="cell-strong" data-testid="summary-offer">
                {{
                  offerTypeLabels[subscription.seat_offer_type || '']
                    ? (offerTypeLabels[subscription.seat_offer_type || ''] | translate)
                    : unresolved
                }}
              </span>
            </div>

            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.subscriptions.summary.current_period' | translate }}</span>
              <span class="cell-strong" data-testid="summary-period">
                {{
                  subscription.current_period_start
                    ? (subscription.current_period_start | date: dateFormat)
                    : unresolved
                }}
                —
                {{
                  subscription.current_period_end
                    ? (subscription.current_period_end | date: dateFormat)
                    : unresolved
                }}
              </span>
            </div>

            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.subscriptions.summary.trial_ends' | translate }}</span>
              <span class="cell-strong" data-testid="summary-trial-end">
                {{
                  subscription.trial_ends_at
                    ? (subscription.trial_ends_at | date: dateFormat)
                    : unresolved
                }}
              </span>
            </div>

            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.subscriptions.summary.last_stripe_event' | translate }}</span>
              <span class="cell-strong">
                {{
                  subscription.last_stripe_event_at
                    ? (subscription.last_stripe_event_at | date: dateTimeFormat)
                    : unresolved
                }}
              </span>
            </div>
          </div>

          <p class="av-meta m-0">{{ 'backoffice.subscriptions.summary.period_note' | translate }}</p>

          @if (subscription.isTrialing) {
            <p class="av-meta m-0" data-testid="summary-trial-note">{{ 'backoffice.subscriptions.summary.trial_note' | translate }}</p>
          }

          @if (subscription.isEndingAtPeriodEnd) {
            <div class="av-banner p-3 d-flex align-items-start gap-2" data-testid="summary-ending">
              <mat-icon aria-hidden="true">event_busy</mat-icon>
              <span>{{ 'backoffice.subscriptions.summary.cancel_at_period_end' | translate }}</span>
            </div>
          }
        </div>

        <div class="av-card p-3 d-flex flex-column gap-2" data-testid="control-commitment">
          <h3 class="av-section-title m-0">{{ 'backoffice.subscriptions.commitment.title' | translate }}</h3>

          <div class="d-flex flex-wrap gap-4">
            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.subscriptions.commitment.committed' | translate }}</span>
              <span class="cell-strong" data-testid="commitment-is-committed">
                {{
                  (subscription.commitment.is_committed
                    ? 'backoffice.common.yes'
                    : 'backoffice.common.no'
                  ) | translate
                }}
              </span>
            </div>

            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.subscriptions.commitment.months' | translate }}</span>
              <span class="cell-strong">
                {{ subscription.commitment.commitment_months ?? unresolved }}
              </span>
            </div>

            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.subscriptions.commitment.term_ends' | translate }}</span>
              <span class="cell-strong" data-testid="commitment-term-end">
                {{
                  subscription.commitment.current_term_end
                    ? (subscription.commitment.current_term_end | date: dateFormat)
                    : unresolved
                }}
              </span>
            </div>
          </div>

          @if (facade.isCommitmentLocked()) {
            <div class="av-banner p-3 d-flex align-items-start gap-2" data-testid="commitment-lock">
              <mat-icon aria-hidden="true">lock</mat-icon>
              <span>{{ 'backoffice.subscriptions.commitment.locked' | translate }}</span>
            </div>
          } @else {
            <p class="av-meta m-0" data-testid="commitment-open">{{ 'backoffice.subscriptions.commitment.open' | translate }}</p>
          }
        </div>

        <div class="av-card p-3 d-flex flex-column gap-3" data-testid="control-change">
          <div class="d-flex flex-column gap-1">
            <h3 class="av-section-title m-0">{{ 'backoffice.subscriptions.change.title' | translate }}</h3>
            <p class="av-meta m-0">{{ 'backoffice.subscriptions.change.hint' | translate }}</p>
          </div>

          @if (facade.plansState() === 'error') {
            <p class="av-meta m-0" data-testid="plans-error">{{ 'backoffice.subscriptions.change.plans_error' | translate }}</p>
          } @else {
            <div class="d-flex flex-wrap align-items-end gap-2">
              <label class="d-flex flex-column gap-1" for="change-plan">
                <span class="av-meta">{{ 'backoffice.subscriptions.change.target_plan' | translate }}</span>
                <select
                  id="change-plan"
                  class="av-field"
                  [value]="facade.targetPlanId()"
                  (change)="selectPlan($any($event.target).value)"
                  data-testid="change-plan-select"
                >
                  <option value="">{{ 'backoffice.subscriptions.change.pick_plan' | translate }}</option>
                  @for (plan of facade.plans(); track plan.id) {
                    <option [value]="plan.id">{{
                      'backoffice.subscriptions.change.plan_option'
                        | translate: { plan: plan.displayName, market: plan.marketLabel }
                    }}</option>
                  }
                </select>
              </label>

              <label class="d-flex flex-column gap-1" for="change-offer">
                <span class="av-meta">{{ 'backoffice.subscriptions.summary.offer' | translate }}</span>
                <select
                  id="change-offer"
                  class="av-field"
                  [value]="facade.targetOfferType()"
                  (change)="selectOfferType($any($event.target).value)"
                  data-testid="change-offer-select"
                >
                  @for (offer of offerTypeOptions; track offer) {
                    <option [value]="offer">{{ offerTypeLabels[offer] | translate }}</option>
                  }
                </select>
              </label>

              <button
                type="button"
                class="av-btn"
                [disabled]="!facade.canPreview() || facade.isPreviewing()"
                (click)="requestPreview()"
                data-testid="change-preview-button"
              >
                {{ 'backoffice.subscriptions.change.preview_button' | translate }}
              </button>

              <button
                type="button"
                class="av-btn av-btn-primary"
                [disabled]="!facade.canApplyChange()"
                (click)="openApplyChange()"
                data-testid="change-apply-button"
              >
                {{ 'backoffice.subscriptions.change.apply_button' | translate }}
              </button>
            </div>

            <p class="av-meta m-0" data-testid="change-preview-required">{{ 'backoffice.subscriptions.change.preview_required' | translate }}</p>

            @if (facade.isPreviewStale()) {
              <div class="av-banner p-3 d-flex align-items-start gap-2" data-testid="preview-stale">
                <mat-icon aria-hidden="true">sync_problem</mat-icon>
                <span>{{ 'backoffice.subscriptions.change.preview_stale' | translate }}</span>
              </div>
            }

            @if (facade.preview(); as preview) {
              <div class="av-card p-3 d-flex flex-column gap-2" data-testid="change-preview">
                <h4 class="av-section-title m-0">{{ 'backoffice.subscriptions.change.preview_title' | translate }}</h4>

                <div class="d-flex flex-wrap gap-4">
                  <div class="d-flex flex-column gap-1">
                    <span class="av-meta">{{ 'backoffice.subscriptions.change.amount_due' | translate }}</span>
                    <span class="cell-strong" data-testid="preview-amount-due">
                      {{ preview.amount_due_now === null ? unresolved : (preview.amount_due_now | number: '1.2-2') }}
                      {{ preview.currency }}
                    </span>
                  </div>

                  <div class="d-flex flex-column gap-1">
                    <span class="av-meta">{{ 'backoffice.subscriptions.change.next_total' | translate }}</span>
                    <span class="cell-strong" data-testid="preview-next-total">
                      {{ preview.next_invoice_total === null ? unresolved : (preview.next_invoice_total | number: '1.2-2') }}
                      {{ preview.currency }}
                    </span>
                  </div>

                  <div class="d-flex flex-column gap-1">
                    <span class="av-meta">{{ 'backoffice.subscriptions.change.proration' | translate }}</span>
                    <span class="cell-strong">{{ preview.proration_behavior }}</span>
                  </div>
                </div>

                <p class="av-meta m-0">{{ 'backoffice.subscriptions.change.items_label' | translate }}</p>

                <ul class="m-0 ps-3">
                  @for (item of preview.items; track $index) {
                    <li class="av-source">
                      {{ item.price || item.id }}
                      @if (item.deleted) {
                        <span>{{ 'backoffice.subscriptions.change.item_removed' | translate }}</span>
                      } @else {
                        <span>{{
                          'backoffice.subscriptions.change.item_quantity'
                            | translate: { quantity: item.quantity }
                        }}</span>
                      }
                    </li>
                  }
                </ul>
              </div>
            }
          }
        </div>

        <div class="av-card p-3 d-flex flex-column gap-3" data-testid="control-actions">
          <h3 class="av-section-title m-0">{{ 'backoffice.subscriptions.actions.title' | translate }}</h3>

          <div class="d-flex flex-wrap gap-2">
            <button
              type="button"
              class="av-btn"
              [disabled]="!facade.canWrite()"
              (click)="openPause()"
              data-testid="action-pause"
            >
              <mat-icon aria-hidden="true">pause_circle</mat-icon>
              <span>{{ 'backoffice.subscriptions.actions.pause' | translate }}</span>
            </button>

            <button
              type="button"
              class="av-btn"
              [disabled]="!facade.canWrite()"
              (click)="openResume()"
              data-testid="action-resume"
            >
              <mat-icon aria-hidden="true">play_circle</mat-icon>
              <span>{{ 'backoffice.subscriptions.actions.resume' | translate }}</span>
            </button>

            <button
              type="button"
              class="av-btn"
              [disabled]="!facade.canWrite() || !facade.isTrialing()"
              [matTooltip]="'backoffice.subscriptions.trial.only_while_trialing' | translate"
              (click)="openTrial()"
              data-testid="action-trial"
            >
              <mat-icon aria-hidden="true">schedule</mat-icon>
              <span>{{ 'backoffice.subscriptions.trial.button' | translate }}</span>
            </button>

            <button
              type="button"
              class="av-btn av-btn-danger"
              [disabled]="!facade.canWrite()"
              (click)="openCancel()"
              data-testid="action-cancel"
            >
              <mat-icon aria-hidden="true">cancel</mat-icon>
              <span>{{ 'backoffice.subscriptions.actions.cancel' | translate }}</span>
            </button>
          </div>

          <p class="av-meta m-0" data-testid="cancel-no-refund-note">{{ 'backoffice.subscriptions.actions.cancel_no_refund' | translate }}</p>
        </div>

        <div class="av-card p-3">
          <av-subscription-items
            [organizationName]="facade.organizationName()"
          ></av-subscription-items>
        </div>
      } @else {
        <av-empty-state
          icon="receipt_long"
          [title]="'backoffice.subscriptions.control.empty_title' | translate"
          [message]="'backoffice.subscriptions.control.empty_message' | translate"
        ></av-empty-state>
      }
    }
    @default {
      <av-empty-state
        icon="hourglass_empty"
        [title]="'backoffice.subscriptions.control.loading_title' | translate"
        [message]="'backoffice.subscriptions.control.loading_message' | translate"
      ></av-empty-state>
    }
  }
</section>
